<script setup lang="ts">
import { defineProps, ref, onMounted, watch, onBeforeUnmount, nextTick } from 'vue'
import type { MarkdownRenderer } from '../core/markdown-renderer'
import { setupCodeBlockActions } from '../core/code-block-actions'

const props = defineProps<{
  renderer: MarkdownRenderer | null
  markdown: string
}>()

const html = ref('')
const error = ref('')
const container = ref<HTMLElement | null>(null)

async function render() {
  if (!props.renderer) return
  try {
    await props.renderer.initialize()
    html.value = await props.renderer.render(props.markdown)
    error.value = ''
  } catch (e) {
    error.value = e instanceof Error ? e.message : '渲染失败'
  }
}

watch(
  () => props.markdown,
  () => render(),
  { immediate: true },
)

watch(
  html,
  async () => {
    if (!container.value) return
    await nextTick()
    setupCodeBlockActions(container.value)
  },
  { flush: 'post' },
)
</script>

<template>
  <div class="renderer" ref="container">
    <div v-if="error" class="renderer__error">
      <exclamation-circle-outlined :style="{ fontSize: '16px' }" />
      <pre>{{ error }}</pre>
    </div>
    <article v-else class="renderer__content" v-html="html"></article>
  </div>
</template>

<style scoped>
:root {
  --color-primary: #1677ff;
  --color-primary-hover: #4096ff;
  --color-primary-active: #0958d9;
  --color-bg: #ffffff;
  --color-bg-secondary: #f5f5f5;
  --color-bg-tertiary: #e8e8e8;
  --color-text: #1f1f1f;
  --color-text-secondary: #595959;
  --color-text-tertiary: #8c8c8c;
  --color-border: #d9d9d9;
  --color-border-hover: #bfbfbf;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px rgba(0, 0, 0, 0.02);
  --shadow-md: 0 6px 16px rgba(0, 0, 0, 0.08), 0 3px 6px rgba(0, 0, 0, 0.04), 0 0 1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08), 0 0 1px rgba(0, 0, 0, 0.06);
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --transition-fast: 150ms ease;
  --transition-normal: 250ms ease;
}

[data-theme="dark"] {
  --color-primary: #177ddc;
  --color-primary-hover: #3b9beb;
  --color-primary-active: #0e63b5;
  --color-bg: #141414;
  --color-bg-secondary: #1f1f1f;
  --color-bg-tertiary: #2d2d2d;
  --color-text: #ffffff;
  --color-text-secondary: #a6a6a6;
  --color-text-tertiary: #737373;
  --color-border: #434343;
  --color-border-hover: #595959;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.2), 0 1px 6px -1px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1);
  --shadow-md: 0 6px 16px rgba(0, 0, 0, 0.3), 0 3px 6px rgba(0, 0, 0, 0.2), 0 0 1px rgba(0, 0, 0, 0.2);
  --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.4), 0 8px 16px rgba(0, 0, 0, 0.3), 0 0 1px rgba(0, 0, 0, 0.2);
}

.renderer {
  padding: 20px;
  max-width: 100%;
  overflow: auto;
}

.renderer__content {
  max-width: 100%;
  color: var(--color-text);
  word-wrap: break-word;
}

.renderer :deep(h1),
.renderer :deep(h2),
.renderer :deep(h3),
.renderer :deep(h4),
.renderer :deep(h5),
.renderer :deep(h6) {
  margin-bottom: 0.5em;
  line-height: 1.3;
  font-weight: 600;
}

.renderer :deep(h1) { font-size: 2em; }
.renderer :deep(h2) { margin-top: 0; font-size: 1.6em; border-bottom: 1px solid var(--color-border); padding-bottom: 0.3em; }
.renderer :deep(h3) { font-size: 1.35em; }

.renderer__state,
.renderer__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  color: var(--color-text-tertiary);
}

.renderer__error {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 8px;
  color: #cf1322;
}

.renderer__error pre {
  white-space: pre-wrap;
  margin-top: 8px;
  font-family: monospace;
}

.renderer__content :deep(p) {
  margin: 1em 0;
}

.renderer__content :deep(.contains-task-list) {
  padding-left: .4em !important;
}

.renderer__content :deep(li) {
  margin: 0.25em 0;
}

.renderer__content :deep(li::marker) {
  color: var(--color-text-tertiary);
}

.renderer__content :deep(hr) {
  margin: .5em 0;
  border: none;
  border-top: 1px solid var(--color-border);
}

.renderer__content :deep(.footnote-ref) {
  top: -0.5em;
  right: -.1em;
}

.renderer__content :deep(table) {
  width: 100%;
  margin: 1.5em 0;
  border-collapse: collapse;
  font-size: 0.9em;
}

.renderer__content :deep(th),
.renderer__content :deep(td) {
  padding: 0.75em 1em;
  border: 1px solid var(--color-border);
  text-align: left;
}

.renderer__content :deep(th) {
  background: var(--color-bg-secondary);
  font-weight: 600;
}

.renderer__content :deep(tr:nth-child(even) td) {
  background: var(--color-bg-secondary);
}

.renderer__content :deep(hr) {
  margin: .5em 0;
  border: none;
  border-top: 1px solid var(--color-border);
}

.renderer__content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
}

.renderer__content :deep(a) {
  color: var(--color-primary);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.renderer__content :deep(a:hover) {
  color: var(--color-primary-hover);
}

.renderer__content :deep(.mathjax-block),
.renderer__content :deep(.MathJax[display="true"]) {
  overflow-x: auto;
  margin: 1em 0;
}

.renderer__content :deep(.mathjax-error) {
  color: #d93025;
  background: rgba(217, 48, 37, 0.08);
  border-radius: 4px;
  padding: 0 0.25em;
  white-space: pre-wrap;
}

.renderer__content :deep(.typst-svg) {
  display: block;
  max-width: 100%;
  height: auto;
  margin: 1.5em auto;
}

.renderer__content :deep(.task-list-item) {
  display: flex;
  align-items: flex-start;
  gap: 0.5em;
  list-style: none;
}

.renderer__content :deep(.task-list-item input[type="checkbox"]) {
  margin-right: 3px;
  accent-color: var(--color-primary);
}

.renderer__content :deep(.footnotes) {
  font-size: 0.875em;
  color: var(--color-text-secondary);
}

.renderer__content :deep(.footnote-backref) {
  margin-left: 0.25em;
  text-decoration: none;
}

.renderer__content :deep(ul),
.renderer__content :deep(ol) {
  padding-left: 1.6em;
  margin: 0.5em 0;
}
</style>